import { useMemo, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Users, ListTodo, Filter } from 'lucide-react';
import type { DeviationFilter } from '@/utils/urlParsing';
import {
  calculateAllDeviations,
  filterDeviations,
  aggregateByAssignee,
  aggregateByTask,
  formatDeviationPercent,
} from '@/utils/deviations';
import { formatDurationForDeviation as formatTime } from '@/utils/duration';
import { CustomFieldsInline } from '@/components/fields/CustomFieldsInline';
import clsx from 'clsx';
import { useDeviationsView } from '@/hooks/useDeviationsView';
import { useTaskActions } from '@/hooks/useTaskActions';

export function DeviationsView() {
  const {
    tasks,
    groupBy,
    filter,
    assigneeFilter,
    onGroupByChange,
    onFilterChange,
    onAssigneeFilterChange,
    onTaskClick,
  } = useDeviationsView();
  const { handleUpdateTask: onUpdateTask } = useTaskActions();
  // Calculate all deviations
  const allDeviations = useMemo(() => {
    return calculateAllDeviations(tasks);
  }, [tasks]);

  // Filter deviations
  const filteredDeviations = useMemo(() => {
    let filtered = filterDeviations(allDeviations, filter);

    // Apply assignee filter if set
    if (assigneeFilter) {
      filtered = filtered.filter((d) => d.assignee === assigneeFilter);
    }

    return filtered;
  }, [allDeviations, filter, assigneeFilter]);

  // Aggregate based on groupBy
  const aggregations = useMemo(() => {
    if (groupBy === 'person') {
      return aggregateByAssignee(filteredDeviations);
    } else if (groupBy === 'task') {
      return aggregateByTask(filteredDeviations);
    }
    // If no grouping, return individual deviations as aggregations
    return filteredDeviations.map((d) => ({
      taskId: d.taskId,
      assignee: d.assignee,
      totalEstimated: d.estimated,
      totalSpent: d.spent,
      totalDeviation: d.deviation,
      deviationPercent: d.deviationPercent,
      taskCount: 1,
      overEstimateCount: d.deviation > 0 ? 1 : 0,
      underEstimateCount: d.deviation < 0 ? 1 : 0,
      onTimeCount: Math.abs(d.deviationPercent) <= 5 ? 1 : 0,
    }));
  }, [filteredDeviations, groupBy]);

  // Get unique assignees for filter dropdown
  const uniqueAssignees = useMemo(() => {
    const assignees = new Set(allDeviations.map((d) => d.assignee));
    return Array.from(assignees).sort();
  }, [allDeviations]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalEstimated = filteredDeviations.reduce((sum, d) => sum + d.estimated, 0);
    const totalSpent = filteredDeviations.reduce((sum, d) => sum + d.spent, 0);
    const totalDeviation = totalSpent - totalEstimated;
    const deviationPercent = totalEstimated > 0 ? (totalDeviation / totalEstimated) * 100 : 0;

    return {
      totalEstimated,
      totalSpent,
      totalDeviation,
      deviationPercent,
      count: filteredDeviations.length,
      overCount: filteredDeviations.filter((d) => d.deviation > 0).length,
      underCount: filteredDeviations.filter((d) => d.deviation < 0).length,
      onTimeCount: filteredDeviations.filter((d) => Math.abs(d.deviationPercent) <= 5).length,
    };
  }, [filteredDeviations]);

  const handleTaskClick = useCallback(
    (taskId: string) => {
      onTaskClick?.(taskId);
    },
    [onTaskClick]
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface-primary">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border-primary bg-surface-secondary px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-text-primary">Deviazioni Stime</h1>
            <p className="text-sm text-text-tertiary mt-1">
              Analisi delle deviazioni tra tempo stimato e tempo effettivo
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4 mt-4">
          {/* Group By */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-text-secondary">Raggruppa per:</label>
            <div className="flex gap-1 bg-surface-tertiary rounded-lg p-1">
              <button
                onClick={() => onGroupByChange(null)}
                className={clsx(
                  'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                  groupBy === null
                    ? 'bg-accent-violet text-white'
                    : 'text-text-tertiary hover:text-text-primary hover:bg-surface-hover'
                )}
              >
                Nessuno
              </button>
              <button
                onClick={() => onGroupByChange('person')}
                className={clsx(
                  'px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5',
                  groupBy === 'person'
                    ? 'bg-accent-violet text-white'
                    : 'text-text-tertiary hover:text-text-primary hover:bg-surface-hover'
                )}
              >
                <Users className="w-3 h-3" />
                Persona
              </button>
              <button
                onClick={() => onGroupByChange('task')}
                className={clsx(
                  'px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5',
                  groupBy === 'task'
                    ? 'bg-accent-violet text-white'
                    : 'text-text-tertiary hover:text-text-primary hover:bg-surface-hover'
                )}
              >
                <ListTodo className="w-3 h-3" />
                Task
              </button>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-text-tertiary" />
            <div className="flex gap-1 bg-surface-tertiary rounded-lg p-1">
              {(['all', 'over', 'under', 'on-time'] as DeviationFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => onFilterChange(f)}
                  className={clsx(
                    'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                    filter === f
                      ? 'bg-accent-violet text-white'
                      : 'text-text-tertiary hover:text-text-primary hover:bg-surface-hover'
                  )}
                >
                  {f === 'all'
                    ? 'Tutti'
                    : f === 'over'
                      ? 'Sopra'
                      : f === 'under'
                        ? 'Sotto'
                        : 'In tempo'}
                </button>
              ))}
            </div>
          </div>

          {/* Assignee Filter */}
          {groupBy !== 'person' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-text-secondary">Persona:</label>
              <select
                value={assigneeFilter || ''}
                onChange={(e) => onAssigneeFilterChange(e.target.value || null)}
                className="px-3 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-violet/30"
              >
                <option value="">Tutte</option>
                {uniqueAssignees.map((assignee) => (
                  <option key={assignee} value={assignee}>
                    {assignee}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-border-primary">
          <div className="text-xs text-text-tertiary">
            <span className="font-medium text-text-secondary">Totale:</span> {summary.count}{' '}
            deviazioni
          </div>
          <div className="text-xs text-text-tertiary">
            <span className="font-medium text-text-secondary">Stimato:</span>{' '}
            {formatTime(summary.totalEstimated)}
          </div>
          <div className="text-xs text-text-tertiary">
            <span className="font-medium text-text-secondary">Speso:</span>{' '}
            {formatTime(summary.totalSpent)}
          </div>
          <div
            className={clsx(
              'text-xs font-medium',
              summary.totalDeviation >= 0 ? 'text-accent-rose' : 'text-accent-green'
            )}
          >
            <span className="text-text-secondary">Deviazione:</span>{' '}
            {summary.totalDeviation >= 0 ? '+' : ''}
            {formatTime(summary.totalDeviation)} ({formatDeviationPercent(summary.deviationPercent)}
            )
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-4">
          {aggregations.length === 0 ? (
            <div className="text-center py-12 text-text-tertiary">
              <p className="text-sm">Nessuna deviazione trovata con i filtri selezionati</p>
            </div>
          ) : (
            <div className="space-y-3">
              {aggregations.map((agg, index) => {
                const isOver = agg.totalDeviation > 0;
                const isUnder = agg.totalDeviation < 0;
                const isOnTime = Math.abs(agg.deviationPercent) <= 5;

                return (
                  <div
                    key={index}
                    className={clsx(
                      'border border-border-primary rounded-lg p-4 bg-surface-secondary hover:bg-surface-tertiary transition-colors',
                      groupBy === 'task' && agg.taskId ? 'cursor-pointer' : ''
                    )}
                    onClick={() => {
                      if (agg.taskId) {
                        handleTaskClick(agg.taskId);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {groupBy === 'person' && agg.assignee && (
                            <h3 className="font-medium text-text-primary">{agg.assignee}</h3>
                          )}
                          {groupBy === 'task' && agg.taskId && (
                            <>
                              <h3 className="font-medium text-text-primary">
                                {tasks.find((t) => t.id === agg.taskId)?.name || agg.taskId}
                              </h3>
                              {(() => {
                                const task = tasks.find((t) => t.id === agg.taskId);
                                return task ? (
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <CustomFieldsInline
                                      task={task}
                                      onUpdate={
                                        onUpdateTask
                                          ? (updates) => onUpdateTask(task.id, updates)
                                          : undefined
                                      }
                                      maxDisplay={2}
                                      size="sm"
                                    />
                                  </div>
                                ) : null;
                              })()}
                            </>
                          )}
                          {groupBy === null && (
                            <>
                              <h3 className="font-medium text-text-primary">
                                {tasks.find((t) => t.id === agg.taskId)?.name || agg.taskId}
                              </h3>
                              <span className="text-xs text-text-tertiary">•</span>
                              <span className="text-sm text-text-tertiary">{agg.assignee}</span>
                              {(() => {
                                const task = tasks.find((t) => t.id === agg.taskId);
                                return task ? (
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <CustomFieldsInline
                                      task={task}
                                      onUpdate={
                                        onUpdateTask
                                          ? (updates) => onUpdateTask(task.id, updates)
                                          : undefined
                                      }
                                      maxDisplay={2}
                                      size="sm"
                                    />
                                  </div>
                                ) : null;
                              })()}
                            </>
                          )}
                        </div>

                        <div className="flex items-center gap-4 text-xs text-text-tertiary">
                          <span>
                            Stimato:{' '}
                            <span className="font-medium text-text-secondary">
                              {formatTime(agg.totalEstimated)}
                            </span>
                          </span>
                          <span>
                            Speso:{' '}
                            <span className="font-medium text-text-secondary">
                              {formatTime(agg.totalSpent)}
                            </span>
                          </span>
                          <span
                            className={clsx(
                              'font-medium',
                              isOver
                                ? 'text-accent-rose'
                                : isUnder
                                  ? 'text-accent-green'
                                  : 'text-text-secondary'
                            )}
                          >
                            {isOver ? '+' : ''}
                            {formatTime(agg.totalDeviation)} (
                            {formatDeviationPercent(agg.deviationPercent)})
                          </span>
                        </div>

                        {groupBy !== null && (
                          <div className="flex items-center gap-4 mt-2 text-xs text-text-quaternary">
                            <span>{agg.taskCount} task</span>
                            <span className="text-accent-rose">{agg.overEstimateCount} sopra</span>
                            <span className="text-accent-green">
                              {agg.underEstimateCount} sotto
                            </span>
                            <span className="text-text-tertiary">{agg.onTimeCount} in tempo</span>
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0 ml-4">
                        {isOver && <TrendingUp className="w-5 h-5 text-accent-rose" />}
                        {isUnder && <TrendingDown className="w-5 h-5 text-accent-green" />}
                        {isOnTime && <Minus className="w-5 h-5 text-text-tertiary" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
